import { FC, useState, useRef, useEffect } from "react";
import CommonLayout from "./CommonLayout";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { selectCurrentExperience, setExperience } from "application/features/experience/experienceSlice";
import { useUploadExperienceAlbumImageMutation, useUploadExperienceCoverMutation } from "application/features/experience/experienceApiSlice";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import InfoTooltip from "components/Quasi/InfoTooltip/InfoTooltip";

export interface PageAddExperience4Props { }

const PageAddExperience4: FC<PageAddExperience4Props> = () => {
  const [cover, setCover] = useState<string>("");
  const [album, setAlbum] = useState<Array<string>>([]);

  const coverRef = useRef<HTMLInputElement>(null);

  const experience = useSelector(selectCurrentExperience)
  const [updateCover, { isLoading: uploadingCover }] = useUploadExperienceCoverMutation()
  const [updateAlbumImage, { isLoading: uploadingAlbumImage }] = useUploadExperienceAlbumImageMutation()
  const dispatch = useDispatch()

  const uploadCover = async (e: any) => {
    try {
      // if file size is greater than 10mb throw error
      if (e.target.files[0].size > 10485760) {
        throw alert('Inserisci un file di dimensioni inferiori a 10mb')
      }
      const url = URL.createObjectURL(e.target.files[0]);
      setCover(url)

      const formData = new FormData()
      formData.append('experience-cover', e.target.files[0])

      const response = await updateCover(formData).unwrap();
      dispatch(setExperience({ ...experience, cover: response.result.s3Location }))
    } catch (err) {
      console.log(err)
    }
  }

  const uploadAlbumImage = async (e: any) => {
    try {
      const files = Array.from(e.target.files) as File[];

      // Validate all files
      const oversizedFiles = files.filter((file: File) => file.size > 10485760);
      if (oversizedFiles.length > 0) {
        alert(`${oversizedFiles.length} file(s) exceed 10MB limit and will be skipped`);
      }

      const validFiles = files.filter((file: File) => file.size <= 10485760);

      // Create preview URLs for all files
      const previewUrls = validFiles.map((file: File) => URL.createObjectURL(file));
      setAlbum([...album, ...previewUrls]);

      // Upload all files sequentially and accumulate results
      let uploadedImages = [...(experience?.gallery_images || [])];

      for (const file of validFiles) {
        const formData = new FormData();
        formData.append('experience-album-image', file);

        const response = await updateAlbumImage(formData).unwrap();
        uploadedImages.push(response.result.s3Location);

        dispatch(setExperience({ ...experience, gallery_images: [...uploadedImages] }));
      }
    } catch (err) {
      console.log(err)
    }
  }

  const handleRemoveCover = () => {
    if (coverRef.current) {
      coverRef.current.value = "";
    }
    setCover("")
    dispatch(setExperience({ ...experience, cover: null }))
  }

  const CustomPicker = (url: string) => {
    if (url && url.length > 0) {
      return (
        <div>
          <img src={url} alt="" className="w-auto max-h-80" />
          <div className="absolute top-0 right-0 z-50">
            <button
              onClick={() => {
                handleRemoveCover()
              }}
              className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-1"
            >
              <svg
                className="w-4 h-4 text-neutral-6000 dark:text-neutral-300"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      )
    }
    else {
      return (
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
          <div
            className="space-y-1 text-center relative">
            <svg
              className="mx-auto h-12 w-12 text-neutral-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
            <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
              <label
                htmlFor="file-upload"
                className="relative flex justify-center cursor-pointer text-center  rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <span>Carica qui</span>
              </label>
              <p className="pl-1">il tuo file</p>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              PNG, JPG, JPEG non più di 10MB
            </p>
          </div>
        </div>
      )
    }
  }

  useEffect(() => {
    if (!experience?.gallery_images && !experience?.media) {
      dispatch(setExperience({ ...experience, gallery_images: [] }))
    }
    // if older objects have media, migrate to gallery_images in store for consistency
    if (!experience?.gallery_images && experience?.media) {
      dispatch(setExperience({ ...experience, gallery_images: experience.media }))
    }
  }, [])

  return (
    <CommonLayout
      index="04"
      backtHref="/host/new-experience/add-listing-3"
      nextHref="/host/new-experience/add-listing-5"
      canGoNext={experience?.cover?.length > 0 && !uploadingCover && !uploadingAlbumImage}
    >
      <>
        <div>
          <h2 className="text-2xl font-semibold">Foto dell'esperienze</h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            Qualche foto dell'esperienza per farla conoscere meglio ai tuoi clienti,
            renderà il tuo annuncio più accattivante.
          </span>
        </div>

        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {/* FORM */}
        <div className="space-y-8">
          <div>
            <span className="text-lg font-semibold inline-flex items-center">
              Immagine di copertina
              <InfoTooltip className="ml-2" i18nKey="translation:tooltips.experience_upload.immagine_di_copertina" />
            </span>
            <div className="mt-5 relative ">
              {
                uploadingCover ?
                  <div className="w-full h-32 ">
                    <Skeleton className="h-full rounded-full" />
                  </div>
                  :
                  CustomPicker(cover || experience?.cover || "")
              }
              <input
                disabled={uploadingCover}
                type="file"
                ref={coverRef}
                accept="image/*"
                name="experience-cover"
                className="absolute w-full h-full z-40 opacity-0 top-0"
                onChange={(e) => e.target.files && uploadCover(e)}
              />
            </div>
          </div>
          {/* ----------------- */}
          <div>
            <span className="text-lg font-semibold">Foto dell'annuncio</span>
            <div className="mt-5 relative ">
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-neutral-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                  <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                    <label
                      htmlFor="file-upload-2"
                      className="relative cursor-pointer  rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                    >
                      <span>Upload a file</span>
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>
              <input
                id="file-upload-2"
                type="file"
                accept="image/*"
                multiple
                name="property-album-image"
                className="absolute w-full h-full z-40 opacity-0 top-0"
                onChange={(e) => e.target.files && uploadAlbumImage(e)}
              />
            </div>
            <div className="w-full mt-2">
              {
                uploadingAlbumImage ?
                  <div className="w-full h-32 ">
                    <Skeleton className="h-full rounded-full" />
                  </div>
                  :
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {
                      (experience?.gallery_images || experience?.media || []).map((item: string, index: number) => (
                        <div key={index} className="relative">
                          <img src={item} alt="" className="w-full h-32 object-cover rounded-md" />
                          <div className="absolute top-0 right-0">
                            <button
                              onClick={() => {
                                const current = experience?.gallery_images || experience?.media || [];
                                const updatedAlbum = current.filter((i: string) => i !== item)
                                dispatch(setExperience({ ...experience, gallery_images: updatedAlbum }))
                              }}
                              className="bg-neutral-100 dark:bg-neutral-800 rounded-full p-1"
                            >
                              <svg
                                className="w-4 h-4 text-neutral-6000 dark:text-neutral-300"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M6 18L18 6M6 6l12 12"
                                ></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    }
                  </div>
              }
            </div>
          </div>
        </div>
        {/* PREVIEW */}
        <div className="flex flex-col gap-4 mt-8">
          <h3 className="text-xl font-semibold">Anteprima</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Ecco come apparirà il tuo annuncio.</p>
          <div className="mt-4">
            <MultiStockCard
              id={experience?.experience_uuid || "preview"}
              cover={experience?.cover || ""}
              images={experience?.gallery_images || []}
              title={experience?.name || ""}
              description={experience?.description || ""}
              address={experience?.address || ""}
              price={experience?.price || 0}
              rating={experience?.rating || 0}
              reviewCount={experience?.reviews_count || 0}
              max_guests={experience?.max_guests || 0}
              minutes_duration={experience?.minutes_duration || 0}
              href="#"
              orientation="horizontal"
            />
          </div>
        </div>
      </>
    </CommonLayout>
  );
};

export default PageAddExperience4;
